import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronRight, Clock, Info } from 'lucide-react';

const TONE = {
  action: { icon: AlertTriangle, chip: 'bg-rose-50 text-rose-500' },
  warning: { icon: Clock, chip: 'bg-amber-50 text-amber-600' },
  info: { icon: Info, chip: 'bg-slate-100 text-slate-400' },
};

/** Live "what needs me today" worklist — never bound to the period filter. */
export const ActionQueue = ({ items = [] }) => {
  const navigate = useNavigate();
  const open = items.filter((item) => item.count > 0);
  const total = open.reduce((sum, item) => sum + item.count, 0);

  return (
    <section
      className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-18px_rgba(16,24,40,0.16)]"
      data-testid="action-queue"
    >
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          <AlertTriangle className="h-4 w-4 text-rose-500" strokeWidth={1.75} /> Needs attention
        </h2>
        <span className="font-mono text-xs tabular-nums text-slate-400" data-testid="action-queue-total">
          {total} open item{total === 1 ? '' : 's'}
        </span>
      </header>
      {!open.length ? (
        <div className="flex items-center gap-3 px-5 py-12 text-sm text-slate-500" data-testid="action-queue-clear">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" strokeWidth={1.75} />
          Nothing waiting on you — the pipeline is clear.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {open.map((item) => {
            const tone = TONE[item.tone] || TONE.info;
            const Icon = tone.icon;
            return (
              <li key={item.key}>
                <button
                  type="button"
                  onClick={() => navigate(item.link)}
                  className="group flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors duration-150 hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500/40"
                  data-testid={`action-queue-${item.key}`}
                >
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tone.chip}`}>
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                  </span>
                  <span className="flex-1 text-sm text-slate-700">{item.label}</span>
                  <span className="font-mono text-lg font-bold tabular-nums text-slate-900">{item.count}</span>
                  <ChevronRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-blue-500" strokeWidth={1.75} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
